import { useEffect, useRef, useState } from 'react';

import { extractStoragePath, getAttachmentUrl } from '../services/storage/attachmentUrl';

// Refresh a bit before the signed URL actually expires, so a component that
// stays mounted for a while (the header logo, an open attachments dialog)
// never has a moment where the URL it is rendering has already gone dead.
const REFRESH_MARGIN_MS = 60 * 1000;
const MIN_REFRESH_DELAY_MS = 15 * 1000;

/**
 * #2109 — resolves a stored `photoUrl`/`logoUrl`/`downloadURL`/`storagePath`
 * value into a live, authorized, short-lived URL, and keeps it fresh for as
 * long as the component stays mounted. Replaces rendering a persisted URL
 * directly, which is what silently broke (or, before this ticket, silently
 * stayed public forever) once uploads stopped calling `makePublic()`.
 *
 * @param {?string} storedValue A `photoUrl`/`logoUrl`/`downloadURL`/`storagePath` field.
 * @return {{url: ?string, loading: boolean, error: ?Error}}
 */
export default function useAttachmentUrl(storedValue) {
  const [state, setState] = useState({ url: null, loading: Boolean(storedValue), error: null });
  const timerRef = useRef(null);

  useEffect(() => {
    const storagePath = extractStoragePath(storedValue);
    let cancelled = false;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!storagePath) {
      setState({ url: null, loading: false, error: null });
      return undefined;
    }

    setState((prev) => ({ ...prev, loading: true }));

    async function fetchAndScheduleRefresh() {
      try {
        const { url, expiresAt } = await getAttachmentUrl(storagePath);
        if (cancelled) return;
        setState({ url, loading: false, error: null });
        const delay = Math.max((expiresAt || 0) - Date.now() - REFRESH_MARGIN_MS, MIN_REFRESH_DELAY_MS);
        timerRef.current = setTimeout(fetchAndScheduleRefresh, delay);
      } catch (error) {
        if (cancelled) return;
        setState({ url: null, loading: false, error });
      }
    }

    fetchAndScheduleRefresh();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [storedValue]);

  return state;
}
